import React, { useState } from 'react'
import { Modal, Input, Button, message } from 'antd'
import { AiOutlineRobot } from 'react-icons/ai'
import { refineAIRecommendation } from '../../../api/assessmentReview'
import styles from './FeedbackModal.module.css'
import styled from 'styled-components'

const { TextArea } = Input

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const SectionTitle = styled.h4`
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
`

const RecommendationContainer = styled.div`
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
`

const RefinedRecommendationContainer = styled(RecommendationContainer)`
  background-color: #e6f7ff;
`

const PromptArea = styled(TextArea)`
  font-size: 13px;
`

const RefineWithAIModal = ({
  visible,
  onCancel = () => {},
  onRefineComplete,
  person,
  arDetail,
  recommendationType,
  currentContent,
  title = 'Refine with AI',
  buttonText = 'Refine with AI'
}) => {
  const [isModalVisible, setIsModalVisible] = useState(visible)

  const handleOpenModal = () => {
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setRefinementPrompt('')
    setRefinedContent('')
    setShowAcceptDecline(false)
    onCancel()
  }
  const [refinementPrompt, setRefinementPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [refinedContent, setRefinedContent] = useState('')
  const [showAcceptDecline, setShowAcceptDecline] = useState(false)

  const handleRefine = async () => {
    if (!refinementPrompt.trim()) {
      message.error('Please enter a refinement prompt')
      return
    }

    setLoading(true)
    try {
      const response = await refineAIRecommendation(
        person.id,
        arDetail.id,
        recommendationType,
        currentContent,
        refinementPrompt,
      )

      const content = response.refined_content || ''
      setRefinedContent(content)
      setShowAcceptDecline(true)
    } catch (error) {
      message.error('Failed to refine recommendation. Please try again.')
      console.error('Error refining recommendation:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = () => {
    onRefineComplete(refinedContent)
    handleCloseModal()
  }

  const handleDecline = () => {
    setShowAcceptDecline(false)
    setRefinedContent('')
    setRefinementPrompt('')
  }

  return (
    <>
      <button
        className={styles.reviewButton}
        onClick={handleOpenModal}
        style={{ marginBottom: '16px' }}
      >
        <AiOutlineRobot className={styles.logoIcon} />
        {buttonText}
      </button>

      <Modal
        title={title}
        visible={isModalVisible}
        onCancel={handleCloseModal}
        width={700}
        bodyStyle={{
          padding: '24px'
        }}
        footer={
          <FooterContainer>
            {!showAcceptDecline ? (
              <>
                <Button onClick={handleCloseModal} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleRefine}
                  loading={loading}
                  disabled={!refinementPrompt.trim()}
                >
                  Refine
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleDecline}>Decline</Button>
                <Button type="primary" onClick={handleAccept}>
                  Accept
                </Button>
              </>
            )}
          </FooterContainer>
        }
      >
        {!showAcceptDecline ? (
          <>
            <Section>
              <SectionTitle>Current Recommendation:</SectionTitle>
              <RecommendationContainer
                dangerouslySetInnerHTML={{
                  __html: currentContent || 'No content available'
                }}
              />
            </Section>

            <div>
              <SectionTitle>Refinement Prompt:</SectionTitle>
              <PromptArea
                value={refinementPrompt}
                onChange={(e) => setRefinementPrompt(e.target.value)}
                placeholder="Enter your refinement request (e.g., 'Make this more detailed', 'Focus on nutrition', 'Add exercise recommendations')"
                rows={4}
              />
            </div>
          </>
        ) : (
          <>
            <Section>
              <SectionTitle>Original Recommendation:</SectionTitle>
              <RecommendationContainer
                dangerouslySetInnerHTML={{
                  __html: currentContent || 'No content available'
                }}
              />
            </Section>

            <div>
              <SectionTitle>Refined Recommendation:</SectionTitle>
              <RefinedRecommendationContainer
                dangerouslySetInnerHTML={{
                  __html: refinedContent || 'No refined content available'
                }}
              />
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

export default RefineWithAIModal
